/**
 * particles.ts
 *
 * Renders the wind particle trails and head markers -- a short fading motion trail per
 * particle (like the classic earth.nullschool wind animation) colored by temperature.
 *
 * This module is RENDER-ONLY. The actual physics (wind advection, low-pressure persistence
 * tagging, density-targeted respawning, terrain soft-floor, fade in/out lifecycle) runs in a
 * dedicated Web Worker (see workers/particlePhysicsWorker.ts and workers/particlePhysicsCore.ts
 * for that logic, which used to live in this file) so it no longer competes with rendering for
 * main-thread time every frame -- that CPU contention was the hard ceiling on how many
 * particles could be simulated at a smooth frame rate. This module just reads the worker's
 * output every frame from SharedArrayBuffer-backed arrays (see sharedParticleBuffers.ts, which
 * documents exactly which fields are shared and why) and builds/uploads the GPU geometry.
 */

import * as THREE from "three";
import { temperatureToColor } from "../colorScales";
import type { SharedParticleBuffers } from "./sharedParticleBuffers";
import {
  HEAD_POINT_MAX_SIZE_PX,
  HEAD_POINT_MIN_SIZE_PX,
  HEAD_POINT_REFERENCE_SPEED_MS,
  PARTICLE_MAX_OPACITY,
  PARTICLE_TRAIL_LENGTH,
  TRAIL_WIDTH_FRACTION_OF_HEAD_SIZE
} from "../constants";

const SEGMENTS_PER_PARTICLE = PARTICLE_TRAIL_LENGTH - 1;

/** Shared by the head marker and the trail (whose width is a fraction of it) so both read as
 *  one continuous shape per particle rather than a mismatched fat dot on a thin tail. */
function headSizeForSpeedPx(speedMs: number): number {
  const speedT = HEAD_POINT_REFERENCE_SPEED_MS / (speedMs + HEAD_POINT_REFERENCE_SPEED_MS);
  return HEAD_POINT_MIN_SIZE_PX + (HEAD_POINT_MAX_SIZE_PX - HEAD_POINT_MIN_SIZE_PX) * speedT;
}

// Trail-age fade curve (an eased, square-root-like taper -- see writeTrailGeometry()) depends
// only on the small integer slot age (0..PARTICLE_TRAIL_LENGTH-1), which is IDENTICAL for every
// particle at a given age. Precomputed once here instead of calling the transcendental
// Math.pow() for every (particle, segment-end) pair every frame (count * SEGMENTS_PER_PARTICLE
// * 2 calls/frame -- hundreds of thousands -- for what is really only PARTICLE_TRAIL_LENGTH
// distinct values).
const AGE_FADE_CURVE = (() => {
  const curve = new Float32Array(PARTICLE_TRAIL_LENGTH);
  for (let age = 0; age < PARTICLE_TRAIL_LENGTH; age++) {
    curve[age] = Math.pow(1 - age / SEGMENTS_PER_PARTICLE, 0.45);
  }
  return curve;
})();

export interface ParticleRenderConfig {
  /** Renderer's device pixel ratio, so head markers/trails render at a consistent CSS-pixel size. */
  devicePixelRatio: number;
  /** Renderer's drawing-buffer size in device pixels, needed to size trail quads in screen space. */
  viewportWidthPx: number;
  viewportHeightPx: number;
}

export interface ParticleRenderer {
  object3D: THREE.Object3D;
  render(config: ParticleRenderConfig): void;
}

export function createParticleRenderer(buffers: SharedParticleBuffers): ParticleRenderer {
  const count = buffers.count;
  const trailLength = buffers.trailLength;
  const { history, historyColor, headIndex, lifecycleAlpha, temperatureK, horizontalSpeedMs } = buffers;
  const tmpColor = new THREE.Color();

  // Trails need a WIDTH that varies per particle (to match the head marker -- see below), but
  // WebGL's native line width is capped at ~1px on most platforms/drivers, so each trail
  // segment is instead drawn as a small camera-facing quad ("fat line" technique): a single
  // quad template (4 corners, indices set up once) is instanced once per (particle, trail-age)
  // pair, and the vertex shader offsets each corner perpendicular to the segment in SCREEN
  // SPACE by that particle's current half-width in pixels. This also keeps the per-frame CPU
  // cost down (only 1 set of per-segment attributes is written, not 2x/4x duplicated vertex
  // data) compared to writing a literal quad's worth of vertices every frame.
  const quadCornerArray = new Float32Array([
    0, 1, // older endpoint, + side
    0, -1, // older endpoint, - side
    1, 1, // newer endpoint, + side
    1, -1 // newer endpoint, - side
  ]);
  const quadIndexArray = new Uint16Array([0, 1, 2, 1, 3, 2]);
  const instanceCount = count * SEGMENTS_PER_PARTICLE;

  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setAttribute("corner", new THREE.BufferAttribute(quadCornerArray, 2));
  geometry.setIndex(new THREE.BufferAttribute(quadIndexArray, 1));
  geometry.instanceCount = instanceCount;
  geometry.setAttribute("instanceOlderPos", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  geometry.setAttribute("instanceNewerPos", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  // Separate older/newer colors (rather than one shared color) so a trail shows the
  // temperature it actually passed through at each point, not a single color repainted along
  // its whole length every frame -- see historyColor above.
  geometry.setAttribute("instanceColorOlder", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  geometry.setAttribute("instanceColorNewer", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3));
  geometry.setAttribute("instanceAlphaOlder", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount), 1));
  geometry.setAttribute("instanceAlphaNewer", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount), 1));
  geometry.setAttribute("instanceWidth", new THREE.InstancedBufferAttribute(new Float32Array(instanceCount), 1));
  // The geometry has no literal "position" attribute to compute a bounding volume from (the
  // actual positions live in per-instance attributes the frustum-culling code doesn't inspect),
  // so disable automatic culling -- the trail is always roughly globe-sized and typically at
  // least partly in view anyway.
  const material = new THREE.ShaderMaterial({
    transparent: true,
    // Unlike the MSLP shell/coastlines/atmosphere shells (which deliberately skip depthWrite so
    // they don't occlude the particles drawn on top of them -- see globe.ts), particles are the
    // LAST thing drawn (renderOrder 10, nothing after them), so there's nothing further for them
    // to incorrectly occlude. Writing depth here lets the GPU's real z-buffer resolve which
    // particle/trail segment is actually closer to the camera at an overlapping pixel -- without
    // it, "closer wins" was effectively decided by instance submission order instead of true 3D
    // depth, which is why higher-altitude particles could render BEHIND lower-altitude ones
    // (most visible looking straight down, where altitude differences overlap on screen).
    depthWrite: true,
    side: THREE.DoubleSide,
    uniforms: {
      resolution: { value: new THREE.Vector2(1, 1) },
      devicePixelRatio: { value: 1 }
    },
    vertexShader: `
      attribute vec2 corner;
      attribute vec3 instanceOlderPos;
      attribute vec3 instanceNewerPos;
      attribute vec3 instanceColorOlder;
      attribute vec3 instanceColorNewer;
      attribute float instanceAlphaOlder;
      attribute float instanceAlphaNewer;
      attribute float instanceWidth;
      uniform vec2 resolution;
      uniform float devicePixelRatio;
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        vColor = mix(instanceColorOlder, instanceColorNewer, corner.x);
        vAlpha = mix(instanceAlphaOlder, instanceAlphaNewer, corner.x);

        vec3 selfPos = mix(instanceOlderPos, instanceNewerPos, corner.x);
        vec3 otherPos = mix(instanceNewerPos, instanceOlderPos, corner.x);

        vec4 clipSelf = projectionMatrix * modelViewMatrix * vec4(selfPos, 1.0);
        vec4 clipOther = projectionMatrix * modelViewMatrix * vec4(otherPos, 1.0);

        vec2 ndcSelf = clipSelf.xy / clipSelf.w;
        vec2 ndcOther = clipOther.xy / clipOther.w;
        vec2 screenSelf = ndcSelf * resolution * 0.5;
        vec2 screenOther = ndcOther * resolution * 0.5;

        vec2 dir = screenSelf - screenOther;
        float len = length(dir);
        vec2 normal = len > 0.0001 ? normalize(vec2(-dir.y, dir.x)) : vec2(0.0, 1.0);

        float halfWidthPx = instanceWidth * devicePixelRatio * 0.5;
        vec2 ndcOffset = (normal * halfWidthPx * corner.y) / (resolution * 0.5);

        gl_Position = vec4((ndcSelf + ndcOffset) * clipSelf.w, clipSelf.z, clipSelf.w);
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        gl_FragColor = vec4(vColor, vAlpha);
      }
    `
  });
  const lines = new THREE.Mesh(geometry, material);
  lines.frustumCulled = false;
  // Always draw last/on top of other transparent layers (MSLP shell, coastlines, atmosphere
  // shells), regardless of view angle -- see the render-order comment in globe.ts.
  lines.renderOrder = 10;

  // --- Head marker points ------------------------------------------------------
  // A fast particle's fixed-length trail reads as a long streak; a slow particle's reads as a
  // near-invisible dot. To balance how much visual "space" each particle occupies, a round
  // marker is drawn at each particle's current position, sized inversely to its speed (see
  // HEAD_POINT_*_SIZE_PX in constants.ts).
  const headGeometry = new THREE.BufferGeometry();
  headGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  headGeometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  headGeometry.setAttribute("alpha", new THREE.BufferAttribute(new Float32Array(count), 1));
  headGeometry.setAttribute("size", new THREE.BufferAttribute(new Float32Array(count), 1));
  const headMaterial = new THREE.ShaderMaterial({
    vertexColors: true,
    transparent: true,
    // See the matching comment on the trail material above -- same reasoning: heads are the
    // topmost layer, so writing their own depth fixes head-vs-head (and head-vs-trail) stacking
    // to reflect true altitude/camera distance instead of array order.
    depthWrite: true,
    // "size" is a target size in CSS pixels; gl_PointSize is specified in actual framebuffer
    // pixels, so it must be scaled by the renderer's device pixel ratio to render at that
    // visual size. Deliberately NOT distance-attenuated (no projection-based scaling) -- these
    // are UI-style markers meant to stay legible at any zoom level, not world-scale sprites.
    uniforms: { devicePixelRatio: { value: 1 } },
    vertexShader: `
      attribute float alpha;
      attribute float size;
      uniform float devicePixelRatio;
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        vColor = color;
        vAlpha = alpha;
        gl_PointSize = size * devicePixelRatio;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        vec2 fromCenter = gl_PointCoord - vec2(0.5);
        float dist = length(fromCenter);
        if (dist > 0.5) discard;
        float edgeFade = smoothstep(0.5, 0.3, dist);
        gl_FragColor = vec4(vColor, vAlpha * edgeFade);
      }
    `
  });
  const headPoints = new THREE.Points(headGeometry, headMaterial);
  headPoints.renderOrder = 10;

  const object3D = new THREE.Group();
  object3D.add(lines);
  object3D.add(headPoints);

  function writeHeadPoints(devicePixelRatio: number): void {
    headMaterial.uniforms.devicePixelRatio.value = devicePixelRatio;
    const posAttr = headGeometry.getAttribute("position") as THREE.BufferAttribute;
    const colorAttr = headGeometry.getAttribute("color") as THREE.BufferAttribute;
    const alphaAttr = headGeometry.getAttribute("alpha") as THREE.BufferAttribute;
    const sizeAttr = headGeometry.getAttribute("size") as THREE.BufferAttribute;
    const posArray = posAttr.array as Float32Array;
    const colorArray = colorAttr.array as Float32Array;
    const alphaArray = alphaAttr.array as Float32Array;
    const sizeArray = sizeAttr.array as Float32Array;

    for (let i = 0; i < count; i++) {
      const headSlot = headIndex[i];
      const base = (i * trailLength + headSlot) * 3;
      posArray[i * 3] = history[base];
      posArray[i * 3 + 1] = history[base + 1];
      posArray[i * 3 + 2] = history[base + 2];

      temperatureToColor(temperatureK[i], tmpColor);
      colorArray[i * 3] = tmpColor.r;
      colorArray[i * 3 + 1] = tmpColor.g;
      colorArray[i * 3 + 2] = tmpColor.b;

      alphaArray[i] = lifecycleAlpha[i] * PARTICLE_MAX_OPACITY;

      sizeArray[i] = headSizeForSpeedPx(horizontalSpeedMs[i]);
    }
    posAttr.needsUpdate = true;
    colorAttr.needsUpdate = true;
    alphaAttr.needsUpdate = true;
    sizeAttr.needsUpdate = true;
  }

  function writeTrailGeometry(devicePixelRatio: number, viewportWidthPx: number, viewportHeightPx: number): void {
    material.uniforms.devicePixelRatio.value = devicePixelRatio;
    material.uniforms.resolution.value.set(Math.max(1, viewportWidthPx), Math.max(1, viewportHeightPx));

    const olderPosAttr = geometry.getAttribute("instanceOlderPos") as THREE.InstancedBufferAttribute;
    const newerPosAttr = geometry.getAttribute("instanceNewerPos") as THREE.InstancedBufferAttribute;
    const colorOlderAttr = geometry.getAttribute("instanceColorOlder") as THREE.InstancedBufferAttribute;
    const colorNewerAttr = geometry.getAttribute("instanceColorNewer") as THREE.InstancedBufferAttribute;
    const alphaOlderAttr = geometry.getAttribute("instanceAlphaOlder") as THREE.InstancedBufferAttribute;
    const alphaNewerAttr = geometry.getAttribute("instanceAlphaNewer") as THREE.InstancedBufferAttribute;
    const widthAttr = geometry.getAttribute("instanceWidth") as THREE.InstancedBufferAttribute;
    const olderPosArray = olderPosAttr.array as Float32Array;
    const newerPosArray = newerPosAttr.array as Float32Array;
    const colorOlderArray = colorOlderAttr.array as Float32Array;
    const colorNewerArray = colorNewerAttr.array as Float32Array;
    const alphaOlderArray = alphaOlderAttr.array as Float32Array;
    const alphaNewerArray = alphaNewerAttr.array as Float32Array;
    const widthArray = widthAttr.array as Float32Array;

    for (let i = 0; i < count; i++) {
      const head = headIndex[i];
      // Combined fade-in/fade-out opacity, plus the overall opacity cap (see
      // PARTICLE_MAX_OPACITY) so trails blend slightly with the surface rather than reading as
      // solid, flat lines.
      const particleAlpha = lifecycleAlpha[i] * PARTICLE_MAX_OPACITY;
      // Trail width matches a fraction of this particle's current head marker size, so the
      // tail reads as a continuation of the head rather than a fixed-width line (see
      // TRAIL_WIDTH_FRACTION_OF_HEAD_SIZE in constants.ts).
      const widthPx = headSizeForSpeedPx(horizontalSpeedMs[i]) * TRAIL_WIDTH_FRACTION_OF_HEAD_SIZE;
      const particleSlotBase = i * trailLength;

      for (let age = 0; age < SEGMENTS_PER_PARTICLE; age++) {
        const newerSlot = (head - age + trailLength) % trailLength;
        const olderSlot = (head - age - 1 + trailLength) % trailLength;
        const newerBase = (particleSlotBase + newerSlot) * 3;
        const olderBase = (particleSlotBase + olderSlot) * 3;

        const instance = i * SEGMENTS_PER_PARTICLE + age;
        const vec3Base = instance * 3;
        newerPosArray[vec3Base] = history[newerBase];
        newerPosArray[vec3Base + 1] = history[newerBase + 1];
        newerPosArray[vec3Base + 2] = history[newerBase + 2];
        olderPosArray[vec3Base] = history[olderBase];
        olderPosArray[vec3Base + 1] = history[olderBase + 1];
        olderPosArray[vec3Base + 2] = history[olderBase + 2];

        // Each ring-buffer slot's color was already precomputed from the temperature recorded
        // AT that point in the trail's history when the slot was written (see historyColor
        // above) -- a trail visibly shows the thermal history it passed through, e.g. cooling
        // as it crosses a front, rather than repainting its whole length to one color every
        // frame. Just copied here, not recomputed.
        colorNewerArray[vec3Base] = historyColor[newerBase];
        colorNewerArray[vec3Base + 1] = historyColor[newerBase + 1];
        colorNewerArray[vec3Base + 2] = historyColor[newerBase + 2];
        colorOlderArray[vec3Base] = historyColor[olderBase];
        colorOlderArray[vec3Base + 1] = historyColor[olderBase + 1];
        colorOlderArray[vec3Base + 2] = historyColor[olderBase + 2];

        // An eased (square-root-like) curve keeps the trail visibly bright for most of its
        // length before tapering off, closer to the classic earth.nullschool look than a
        // straight linear fade (see AGE_FADE_CURVE).
        alphaNewerArray[instance] = AGE_FADE_CURVE[age] * particleAlpha;
        alphaOlderArray[instance] = AGE_FADE_CURVE[age + 1] * particleAlpha;
        widthArray[instance] = widthPx;
      }
    }
    olderPosAttr.needsUpdate = true;
    newerPosAttr.needsUpdate = true;
    colorOlderAttr.needsUpdate = true;
    colorNewerAttr.needsUpdate = true;
    alphaOlderAttr.needsUpdate = true;
    alphaNewerAttr.needsUpdate = true;
    widthAttr.needsUpdate = true;
  }

  function render(config: ParticleRenderConfig): void {
    writeTrailGeometry(config.devicePixelRatio, config.viewportWidthPx, config.viewportHeightPx);
    writeHeadPoints(config.devicePixelRatio);
  }

  return { object3D, render };
}
